import React, {type ReactNode} from 'react'

interface Props {
    type?: 'button' | 'submit' | 'reset'
    form?: string
    onClick?: () => void
    testId?: string
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'circular'
    color?: 'primary' | 'secondary' | 'warning' | 'neutral'
    outlined?: boolean
    disabled?: boolean
    /**
     * Render disabled state as a neutral grey rather than this `color`'s own
     * pale tint. Opt-in so it only affects call sites that ask for it (e.g.
     * the public registration form, ENG-4017 follow-up), leaving every other
     * screen's disabled buttons unchanged.
     */
    mutedDisabled?: boolean
    flex?: boolean
    children?: ReactNode
    classes?: string[]
}

const Button = (
    {
        type = 'button',
        form,
        size = 'sm',
        color = 'primary',
        outlined = false,
        disabled = false,
        mutedDisabled = false,
        flex = false,
        onClick,
        testId,
        children,
        classes
    }: Props): ReactNode => {
    if (classes == null) {
        classes = []
    }

    const getClass = (): string => {
        classes.push(
            'uppercase', 'font-bold', 'rounded-lg', 'tracking-wide', 'border', 'cursor-pointer',
            // `outline-hidden` (not `outline-none`) keeps a transparent-but-present
            // outline that Windows High Contrast/forced-colors mode still renders —
            // `ring-*` is box-shadow-based and forced-colors mode doesn't render
            // box-shadows, so `outline-none` here would leave those users with no
            // visible focus indicator at all.
            'focus-visible:outline-hidden', 'focus-visible:ring-2',
            'focus-visible:ring-royal-20', 'focus-visible:ring-offset-2',
        )

        if (flex) {
            classes.push('flex', 'items-center')
        }

        switch (size) {
            case 'xs':
                classes.push('px-2', 'py-1', 'text-xs')
                break

            case 'sm':
                classes.push('px-3', 'py-2', 'text-sm')
                break

            case 'md':
                classes.push('px-4', 'py-3')
                break

            case 'lg':
                classes.push('px-5', 'py-4', 'text-lg')
                break

            case 'circular':
                classes.push('p-3')
                break
        }

        if (disabled && mutedDisabled) {
            classes.push('bg-storm-100', 'text-storm-50', 'border-transparent')
            return classes.join(' ')
        }

        switch (color) {
            case 'primary':
                if (disabled) {
                    if (outlined) {
                        classes.push('bg-royal-60', 'text-royal-40')
                    } else {
                        classes.push('bg-storm-90', 'text-royal-30')
                    }
                } else {
                    if (outlined) {
                        classes.push('bg-royal-60', 'border-royal-50', 'hover:border-royal-20', 'text-storm-10', 'focus:text-royal-10', 'focus:border-royal-10')
                    } else {
                        classes.push('bg-royal-10', 'text-royal-60')
                    }
                }

                break

            case 'secondary':
                classes.push('focus:bg-carolina-10')

                if (disabled) {
                    if (outlined) {
                        classes.push('bg-carolina-60', 'text-carolina-40')
                    } else {
                        classes.push('bg-storm-90 text-carolina-30')
                    }
                } else {
                    if (outlined) {
                        classes.push('bg-carolina-60', 'border-carolina-50', 'hover:border-carolina-20', 'text-storm-10', 'focus:text-carolina-10', 'focus:border-carolina-10')
                    } else {
                        classes.push('bg-carolina-10', 'text-carolina-60', 'hover:bg-carolina-20')
                    }
                }

                break

            case 'warning':
                classes.push('focus:bg-gold-10')

                if (disabled) {
                    classes.push('bg-gold-90 text-gold-30')
                } else {
                    classes.push('bg-gold-10', 'text-gold-60', 'hover:bg-gold-20')
                }

                break

            case 'neutral':
                classes?.push('focus:bg-gray-100')

                if (disabled) {
                    classes.push('bg-gray-900 text-gray-300')
                } else {
                    classes.push('bg-gray-100', 'text-gray-600', 'hover:bg-gray-200')
                }

                break
        }

        return classes.join(' ')
    }

    return (
        <button
            onClick={onClick}
            className={getClass()}
            disabled={disabled}
            type={type}
            form={form}
            data-testid={testId}>
            {children}
        </button>
    )
}

export default Button
